import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { useServices, addService, removeService, updateService, StoredService } from '../../hooks/useServices';
import { publishToContentful } from '../../lib/contentful';
import { fileToDataUrl } from '../../hooks/useClients';
import { Plus, X, Trash2, CheckCircle2, AlertCircle, Image, Loader2, Eye, EyeOff, Edit3, Briefcase, Upload } from 'lucide-react';
const inputClass = 'w-full px-3.5 py-2.5 text-sm bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] transition-colors';
function Field({
  label,
  hint,
  required,
  children
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return <div className="space-y-1.5" data-id="element-278">
      <label className="block text-sm font-medium text-gray-700" data-id="element-279">
        {label}
        {required && <span className="text-[#FF6600] ml-1" data-id="element-280">*</span>}
      </label>
      {hint && <p className="text-xs text-gray-400" data-id="element-281">{hint}</p>}
      {children}
    </div>;
}
function Toggle({
  checked,
  onChange,
  label,
  hint
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
}) {
  return <div className="flex items-center justify-between py-3" data-id="element-282">
      <div data-id="element-283">
        <p className="text-sm font-medium text-gray-700" data-id="element-284">{label}</p>
        {hint && <p className="text-xs text-gray-400 mt-0.5" data-id="element-285">{hint}</p>}
      </div>
      <button type="button" onClick={() => onChange(!checked)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${checked ? 'bg-[#00316B]' : 'bg-gray-200'}`} data-id="element-286">
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${checked ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-287" />
      </button>
    </div>;
}
function slugify(text: string) {
  return text.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
}
interface FormState {
  title: string;
  slug: string;
  description: string;
  image: string;
  published: boolean;
  order: number;
}
const defaultForm: FormState = {
  title: '',
  slug: '',
  description: '',
  image: '',
  published: true,
  order: 0
};
export function ServicesTab() {
  const {
    allServices,
    loading
  } = useServices();
  const [form, setForm] = useState<FormState>(defaultForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState('');
  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const value = e.target.value;
    setForm(prev => {
      const next = {
        ...prev,
        [field]: field === 'order' ? parseInt(value) || 0 : value
      };
      if (field === 'title') {
        next.slug = `/services/${slugify(value)}`;
      }
      return next;
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      // Publish to Contentful CMS
      await publishToContentful({
        contentType: 'services',
        serviceName: form.title,
        slug: form.slug,
        description: form.description
      });
      // Also save locally for immediate UI
      if (editingId) {
        updateService(editingId, {
          title: form.title,
          slug: form.slug,
          description: form.description,
          image: form.image,
          published: form.published,
          order: form.order
        });
      } else {
        addService({
          title: form.title,
          slug: form.slug,
          description: form.description,
          image: form.image,
          published: form.published,
          order: form.order || allServices.length
        });
      }
      setStatus('success');
      toast.success('Content Live on CMS!');
      setForm(defaultForm);
      setEditingId(null);
      setTimeout(() => {
        setStatus('idle');
        setShowForm(false);
      }, 2000);
    } catch (err: any) {
      setStatus('error');
      toast.error(err?.message || 'Failed to publish. Please try again.');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };
  const handleEdit = (service: StoredService) => {
    setForm({
      title: service.title,
      slug: service.slug,
      description: service.description,
      image: service.image,
      published: service.published,
      order: service.order
    });
    setEditingId(service.id);
    setShowForm(true);
  };
  const handleCancelEdit = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(false);
  };
  const handleDelete = (id: string) => {
    removeService(id);
    setDeleteConfirm(null);
  };
  return <div className="space-y-6" data-id="element-288">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-289">
        <div data-id="element-290">
          <h3 className="text-lg font-bold text-[#0C0C0C]" data-id="element-291">
            Services ({allServices.length} total)
          </h3>
          <p className="text-sm text-gray-400 mt-0.5" data-id="element-292">
            Manage your service offerings displayed on the homepage.
          </p>
        </div>
        <Button variant="secondary" onClick={() => {
        if (showForm && editingId) {
          handleCancelEdit();
        } else {
          setShowForm(!showForm);
          setEditingId(null);
          setForm({
            ...defaultForm,
            order: allServices.length
          });
        }
      }} leftIcon={showForm ? <X className="w-4 h-4" data-id="element-294" /> : <Plus className="w-4 h-4" data-id="element-295" />} data-id="element-293">
          {showForm ? 'Cancel' : 'New Service'}
        </Button>
      </div>

      {/* Collapsible Form */}
      <AnimatePresence data-id="element-296">
        {showForm && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.25
      }} className="overflow-hidden" data-id="element-297">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6" data-id="element-298">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/60" data-id="element-299">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-300">
                  Service Info
                </p>
              </div>
              <div className="px-6 py-5 space-y-5" data-id="element-301">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-302">
                  <Field label="Title" required data-id="element-303">
                    <input type="text" className={inputClass} placeholder="e.g. Product Design" value={form.title} onChange={set('title')} required data-id="element-304" />
                  </Field>
                  <Field label="Slug" hint="Auto-generated from title" data-id="element-305">
                    <input type="text" className={inputClass} placeholder="e.g. /services/product-design" value={form.slug} onChange={set('slug')} data-id="element-306" />
                  </Field>
                </div>

                <Field label="Description" required hint="Shown on the service card" data-id="element-307">
                  <textarea className={`${inputClass} resize-none`} rows={3} placeholder="Describe the service..." value={form.description} onChange={set('description')} required data-id="element-308" />
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-309">
                  <Field label="Cover Image" hint="JPG or PNG, max 2MB" data-id="element-310">
                    <label htmlFor="service-image-upload" className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${form.image ? 'border-green-300 bg-green-50/30' : 'border-gray-200 bg-gray-50/50 hover:border-[#00316B]/30 hover:bg-[#00316B]/5'}`} data-id="element-311">
                      {uploadingImage ? <div className="flex items-center gap-2 text-gray-400" data-id="element-312">
                          <Loader2 className="w-5 h-5 animate-spin" data-id="element-313" />
                          <span className="text-sm" data-id="element-314">Processing…</span>
                        </div> : form.image ? <div className="flex flex-col items-center gap-2" data-id="element-315">
                          <img src={form.image} alt="Upload preview" className="h-16 w-auto max-w-[200px] object-contain rounded" data-id="element-316" />
                          <span className="text-xs text-green-600 font-medium" data-id="element-317">
                            Click to replace
                          </span>
                        </div> : <div className="flex flex-col items-center gap-2 text-gray-400" data-id="element-318">
                          <Upload className="w-6 h-6" data-id="element-319" />
                          <span className="text-sm font-medium" data-id="element-320">
                            Click to upload
                          </span>
                          <span className="text-xs" data-id="element-321">JPG or PNG</span>
                        </div>}
                      <input id="service-image-upload" type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" className="hidden" onChange={async e => {
                    const file = e.target.files?.[0];
                    setImageError('');
                    if (!file) return;
                    const ACCEPTED = ['image/jpeg', 'image/png'];
                    if (!ACCEPTED.includes(file.type)) {
                      setImageError('Only JPG and PNG files are accepted.');
                      return;
                    }
                    if (file.size > 2 * 1024 * 1024) {
                      setImageError('File is too large. Maximum size is 2MB.');
                      return;
                    }
                    setUploadingImage(true);
                    try {
                      const dataUrl = await fileToDataUrl(file);
                      setForm(prev => ({
                        ...prev,
                        image: dataUrl
                      }));
                    } catch {
                      setImageError('Failed to process image.');
                    } finally {
                      setUploadingImage(false);
                    }
                  }} data-id="element-322" />
                    </label>
                    {imageError && <p className="text-xs text-red-500 flex items-center gap-1.5 mt-2" data-id="element-323">
                        <AlertCircle className="w-3.5 h-3.5" data-id="element-324" />
                        {imageError}
                      </p>}
                  </Field>
                  <Field label="Display Order" hint="Lower numbers appear first" data-id="element-325">
                    <input type="number" className={inputClass} value={form.order} onChange={set('order')} data-id="element-326" />
                  </Field>
                </div>

                {form.image && <div className="rounded-lg overflow-hidden border border-gray-100 aspect-[3/1] bg-gray-50 max-w-md relative group" data-id="element-327">
                    <img src={form.image} alt="Preview" className="w-full h-full object-cover" onError={e => {
                ;
                (e.target as HTMLImageElement).style.display = 'none';
              }} data-id="element-328" />
                    <button type="button" onClick={() => {
                setForm(prev => ({
                  ...prev,
                  image: ''
                }));
                setImageError('');
                const fileInput = document.getElementById('service-image-upload') as HTMLInputElement;
                if (fileInput) fileInput.value = '';
              }} className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-lg text-gray-500 hover:text-red-500 hover:bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-all" title="Remove image" data-id="element-329">
                      <X className="w-4 h-4" data-id="element-330" />
                    </button>
                  </div>}

                <div className="flex items-start gap-2 p-3 bg-blue-50/60 rounded-lg border border-blue-100" data-id="element-331">
                  <Image className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" data-id="element-332" />
                  <div className="text-xs text-blue-700 space-y-1" data-id="element-333">
                    <p className="font-medium" data-id="element-334">Service image guidelines</p>
                    <ul className="list-disc list-inside text-blue-600 space-y-0.5" data-id="element-335">
                      <li data-id="element-336">
                        Recommended size:{' '}
                        <span className="font-medium" data-id="element-337">1800×600px</span> (3:1
                        ratio, wide landscape)
                      </li>
                      <li data-id="element-338">
                        This size works for both full-width and half-width
                        service cards
                      </li>
                      <li data-id="element-339">
                        Formats: <span className="font-medium" data-id="element-340">JPG or PNG</span>
                      </li>
                      <li data-id="element-341">
                        Use high-quality images with a clear focal point in the
                        center
                      </li>
                      <li data-id="element-342">
                        Images will be cropped to fit — avoid important content
                        near edges
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-b border-gray-100 bg-gray-50/60" data-id="element-343">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-344">
                  Visibility
                </p>
              </div>
              <div className="px-6 divide-y divide-gray-100" data-id="element-345">
                <Toggle checked={form.published} onChange={v => setForm(p => ({
              ...p,
              published: v
            }))} label="Published" hint="Show this service on the website" data-id="element-346" />
              </div>

              <div className="px-6 py-5 border-t border-gray-100 flex items-center justify-between" data-id="element-347">
                <AnimatePresence data-id="element-348">
                  {status === 'success' && <motion.div initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0
              }} className="flex items-center gap-2 text-green-600 text-sm" data-id="element-349">
                      <CheckCircle2 className="w-4 h-4" data-id="element-350" />
                      Service {editingId ? 'updated' : 'added'}!
                    </motion.div>}
                </AnimatePresence>
                <div className="flex items-center gap-4 ml-auto" data-id="element-351">
                  <button type="button" onClick={handleCancelEdit} className="text-sm text-gray-400 hover:text-gray-600 transition-colors" data-id="element-352">
                    {editingId ? 'Cancel Edit' : 'Clear form'}
                  </button>
                  <Button type="submit" variant="secondary" isLoading={status === 'loading'} disabled={status === 'loading'} data-id="element-353">
                    {status === 'loading' ? 'Publishing…' : editingId ? 'Update Service' : 'Add Service'}
                  </Button>
                </div>
              </div>
            </form>
          </motion.div>}
      </AnimatePresence>

      {/* Services List */}
      {loading && <div className="flex items-center justify-center py-12 text-gray-400" data-id="element-354">
          <Loader2 className="w-5 h-5 animate-spin mr-2" data-id="element-355" />
          Loading services…
        </div>}

      {!loading && allServices.length === 0 && <div className="text-center py-16 text-gray-400" data-id="element-356">
          <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-40" data-id="element-357" />
          <p className="text-sm font-medium" data-id="element-358">No services yet</p>
          <p className="text-xs mt-1" data-id="element-359">
            Click "New Service" to add your first offering.
          </p>
        </div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-360">
        {allServices.map(service => <motion.div key={service.id} layout initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex gap-4 group" data-id="element-361">
            <div className="w-24 h-24 rounded-lg bg-gray-100 overflow-hidden shrink-0" data-id="element-362">
              {service.image ? <img src={service.image} alt={service.title} className="w-full h-full object-cover" data-id="element-363" /> : <div className="w-full h-full flex items-center justify-center text-gray-400" data-id="element-364">
                  <Briefcase className="w-6 h-6 opacity-50" data-id="element-365" />
                </div>}
            </div>
            <div className="flex-1 min-w-0 py-1" data-id="element-366">
              <div className="flex items-start justify-between gap-2" data-id="element-367">
                <h4 className="font-bold text-gray-900 truncate" data-id="element-368">
                  {service.title}
                </h4>
                <div className="flex gap-1 shrink-0" data-id="element-369">
                  {service.published ? <span className="bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded font-medium uppercase flex items-center gap-1" data-id="element-370">
                      <Eye className="w-3 h-3" data-id="element-371" />
                      Live
                    </span> : <span className="bg-gray-100 text-gray-600 text-[10px] px-2 py-0.5 rounded font-medium uppercase flex items-center gap-1" data-id="element-372">
                      <EyeOff className="w-3 h-3" data-id="element-373" />
                      Draft
                    </span>}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 line-clamp-2" data-id="element-374">
                {service.description}
              </p>

              <div className="mt-auto pt-3 flex items-center justify-between" data-id="element-375">
                <span className="text-xs text-gray-400" data-id="element-376">
                  Order: {service.order}
                </span>
                <div className="flex items-center gap-2" data-id="element-377">
                  <button onClick={() => handleEdit(service)} className="p-1.5 text-gray-400 hover:text-[#00316B] hover:bg-[#00316B]/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100" title="Edit service" data-id="element-378">
                    <Edit3 className="w-4 h-4" data-id="element-379" />
                  </button>
                  {deleteConfirm === service.id ? <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1 shadow-sm" data-id="element-380">
                      <button onClick={() => handleDelete(service.id)} className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600" data-id="element-381">
                        Confirm
                      </button>
                      <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 text-gray-500 text-xs hover:bg-gray-100 rounded" data-id="element-382">
                        Cancel
                      </button>
                    </div> : <button onClick={() => setDeleteConfirm(service.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100" title="Delete service" data-id="element-383">
                      <Trash2 className="w-4 h-4" data-id="element-384" />
                    </button>}
                </div>
              </div>
            </div>
          </motion.div>)}
      </div>

      <p className="text-center text-xs text-gray-400" data-id="element-385">
        Services are published to Contentful CMS.
      </p>
    </div>;
}